import { useCallback, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import {
  MOBILE_BREAKPOINT_PX,
  nearestAnchor,
  nearestCorner,
  type MobileAnchor,
} from '#features/media-window/model';

/** Меньше этого сдвига — это клик по заголовку, а не перетаскивание. */
const CLICK_SLOP_PX = 6;
/** Свайп вниз на столько — сворачиваем окно в пилюлю. */
const COLLAPSE_SWIPE_PX = 80;

/**
 * Снятие незаконченного жеста. Одно на всё приложение: ручка под пальцем одна.
 *
 * Жест заканчивался только по `pointerup`. На телефоне палец часто уходит в `pointercancel` —
 * системный жест от края, долгое нажатие, перехват прокрутки, — и тогда слушатели прошлого жеста
 * оставались висеть на `window`. Каждое следующее касание где угодно двигало коробку и на
 * отпускании считало край от старой точки старта: после нескольких переносов полоса «застывала»
 * и цеплялась только за случайные места, а оживала, лишь когда её разворачивали и сворачивали —
 * новая коробка была уже другим элементом (01.10.2026).
 */
let abortActiveDrag: (() => void) | null = null;

/**
 * Где нажатие остаётся самому элементу: поле ввода, ползунок и всё, что помечено
 * `data-drag-ignore`. Палец на них – это ввод, а не перенос коробки.
 */
const DRAG_IGNORE_SELECTOR =
  'input, textarea, select, [contenteditable="true"], [role="slider"], [data-drag-ignore]';

/**
 * Кнопки и ссылки внутри коробки: жест от них тоже идёт, но `pointerdown` не гасим – иначе
 * мышь не ставит на них фокус, а нажатие должно остаться обычным нажатием, пока палец стоит.
 */
const INTERACTIVE_SELECTOR = 'button, a[href], [role="button"], [role="menuitem"], label';

/**
 * Палец лёг на прокручиваемую середину, которой есть куда прокручиваться: это прокрутка, а не
 * перенос. Пока прокручивать нечего, середина тянет коробку, как любое другое место.
 */
const startsInScroller = (target: Element, root: HTMLElement) => {
  for (let node: Element | null = target; node && node !== root; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return true;
    }
  }
  return false;
};

/**
 * После переноса палец отпускают над какой-нибудь кнопкой, и браузер доводит касание до клика:
 * коробка переехала бы и тут же свернулась или выключила микрофон. Один клик сразу после
 * переноса гасим; если браузер клика не прислал, ловушка снимается сама.
 */
const swallowNextClick = () => {
  const swallow = (click: MouseEvent) => {
    click.stopPropagation();
    click.preventDefault();
    release();
  };
  const timer = window.setTimeout(() => release(), 400);
  const release = () => {
    window.clearTimeout(timer);
    window.removeEventListener('click', swallow, true);
  };
  window.addEventListener('click', swallow, true);
};

type CornerDragOptions = {
  /** Само окно: за ним следит палец, по нему же считается ближайший угол. */
  ref: RefObject<HTMLElement | null>;
  /** Перетаскивать можно только развёрнутое окно — полноэкранное и свёрнутое стоят на месте. */
  canDrag: boolean;
  /** Где окно стоит на телефоне сейчас — от этого зависит, что значит движение вниз. */
  anchor?: MobileAnchor;
  /** Свайп вниз на мобильном. */
  onCollapse: () => void;
  /** Отпустили на десктопе — окно прилипает к ближайшему углу. */
  onCorner: (corner: ReturnType<typeof nearestCorner>) => void;
  /** Отпустили на телефоне — окно прилипает к ближнему краю, верхнему или нижнему. */
  onAnchor?: (anchor: MobileAnchor) => void;
  /** Край на телефоне закреплён (в переписке виджет всегда под шапкой): жест не начинается. */
  locked?: boolean;
};

/**
 * Жест переноса окна: на телефоне окно переносится между нижним и верхним краем экрана, на
 * десктопе живьём следует за курсором и на отпускании прилипает к ближайшему углу.
 *
 * Раньше на телефоне жест умел ровно одно — свернуть окно движением вниз. Уйти окну было некуда,
 * и в чате оно стояло поверх поля ввода: свернуть его значило прервать единственную связь с
 * разговором, а не свернуть — потерять возможность написать. Поэтому движение теперь читается в
 * обе стороны, а сворачивает только явный рывок вниз от нижнего края — там ниже уже ничего нет.
 */
export const useCornerDrag = ({
  ref,
  canDrag,
  anchor = 'bottom',
  onCollapse,
  onCorner,
  onAnchor,
  locked = false,
}: CornerDragOptions) =>
  useCallback(
    (event: ReactPointerEvent) => {
      const el = ref.current;
      if (!el) {
        return;
      }
      // Второй палец на ручке не заводит второй жест; у мыши жест – только левая кнопка.
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) {
        return;
      }
      // Нажатие уже забрал элемент внутри – например, панорама увеличенного видео звонка.
      if (event.defaultPrevented) {
        return;
      }
      /*
        Обработчик может висеть на всей коробке (01.10.2026: полоса и раскрытый виджет на
        телефоне тянутся с любого места). Нажатие из портала – меню, выбор устройства – всплывает
        сюда по дереву React, но коробке не принадлежит; поля и ползунки оставляем им самим.
      */
      const target = event.target instanceof Element ? event.target : null;
      if (target) {
        if (!event.currentTarget.contains(target) || target.closest(DRAG_IGNORE_SELECTOR)) {
          return;
        }
        if (startsInScroller(target, el)) {
          return;
        }
      }
      if (!target?.closest(INTERACTIVE_SELECTOR)) {
        event.preventDefault();
      }
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
      if (!isMobile && !canDrag) {
        return;
      }
      if (isMobile && locked) {
        return;
      }

      abortActiveDrag?.();

      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      let moved = 0;

      const onMove = (move: PointerEvent) => {
        // Второй палец на экране – не этот жест.
        if (move.pointerId !== pointerId) {
          return;
        }
        const dx = move.clientX - startX;
        const dy = move.clientY - startY;
        moved = Math.abs(dx) + Math.abs(dy);
        el.style.transition = 'none';
        if (isMobile) {
          // Вбок двигать некуда — окно во всю ширину, — а по вертикали теперь в обе стороны.
          el.style.transform = `translateY(${dy}px)`;
        } else {
          el.style.transform = `translate(${dx}px, ${dy}px)`;
        }
      };

      const stop = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        el.style.transform = '';
        el.style.transition = '';
        if (abortActiveDrag === stop) {
          abortActiveDrag = null;
        }
      };

      // Палец отобрала система — коробка возвращается на место, край не меняется.
      const onCancel = (cancel: PointerEvent) => {
        if (cancel.pointerId === pointerId) {
          stop();
        }
      };

      const onUp = (up: PointerEvent) => {
        if (up.pointerId !== pointerId) {
          return;
        }
        const rect = el.getBoundingClientRect();
        stop();
        if (moved >= CLICK_SLOP_PX) {
          swallowNextClick();
        }

        if (isMobile) {
          const dy = up.clientY - startY;
          const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
          const next = nearestAnchor(rect.top + rect.height / 2, viewportHeight);

          // Рывок вниз от нижнего края сворачивает — под окном там уже нет экрана, и другого
          // смысла у жеста быть не может. Тот же рывок от верхнего края просто возвращает окно
          // вниз, иначе перенос наверх оказался бы дорогой в один конец.
          if (dy > COLLAPSE_SWIPE_PX && anchor === 'bottom' && next === 'bottom') {
            onCollapse();
            return;
          }
          if (moved >= CLICK_SLOP_PX && next !== anchor) {
            onAnchor?.(next);
          }
          return;
        }

        if (moved < CLICK_SLOP_PX) {
          return;
        }
        onCorner(nearestCorner(rect.left + rect.width / 2, rect.top + rect.height / 2));
      };

      abortActiveDrag = stop;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
    },
    [ref, canDrag, anchor, onCollapse, onCorner, onAnchor, locked]
  );
